import { ChevronDown, Menu, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { MODULES } from "@/data/modules";
import { useLogout, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  href: string;
}

const PARTICIPANT_NAV_ITEMS: NavItem[] = [
  { label: "Program", href: "/program" },
  { label: "Modules", href: "/modules/1" },
  { label: "Tools", href: "/tools" },
  { label: "Alumni", href: "/alumni" },
];

const FACILITATOR_NAV_ITEMS: NavItem[] = [
  { label: "Program", href: "/program" },
  { label: "Modules", href: "/modules/1" },
  { label: "Tools", href: "/tools" },
  { label: "Planning", href: "/facilitator" },
];

/** Desktop "Modules" nav item — a dropdown that jumps straight to any module. */
function ModulesMenu({
  isFacilitatorView,
  isActive,
}: {
  isFacilitatorView: boolean;
  isActive: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors",
          isFacilitatorView
            ? isActive
              ? "text-accent-foreground border-accent-light border-b-2"
              : "text-accent-light hover:text-accent-foreground"
            : isActive
              ? "text-primary border-primary border-b-2"
              : "text-muted hover:text-foreground",
        )}
      >
        Modules
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute top-full left-0 z-20 mt-1 w-64 overflow-hidden rounded-lg border shadow-lg",
            isFacilitatorView ? "border-accent-light/30 bg-accent" : "border-border bg-white",
          )}
        >
          {MODULES.map((m) => (
            <Link
              key={m.id}
              to={`/modules/${m.id}`}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={cn(
                "block px-4 py-2.5 transition-colors",
                isFacilitatorView ? "hover:bg-accent-light/10" : "hover:bg-primary/5",
              )}
            >
              <span
                className={cn(
                  "block text-sm font-medium",
                  isFacilitatorView ? "text-accent-foreground" : "text-foreground",
                )}
              >
                Module {m.id}
              </span>
              <span
                className={cn(
                  "block text-xs",
                  isFacilitatorView ? "text-accent-light" : "text-muted",
                )}
              >
                {m.title}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();
  const { data: me } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async (): Promise<void> => {
    await logoutMutation.mutateAsync();
    // Program is public now — logging out should drop you into the open
    // participant view, not force you straight back to a login wall.
    navigate("/program");
  };

  const isNavActive = (href: string): boolean => {
    if (href === "/modules/1") {
      return location.pathname.startsWith("/modules");
    }
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const isFacilitatorView = me?.role === "facilitator" || me?.role === "admin";
  const allNavItems = isFacilitatorView ? FACILITATOR_NAV_ITEMS : PARTICIPANT_NAV_ITEMS;

  return (
    <div className="bg-background min-h-screen">
      <header
        className={cn(
          "sticky top-0 z-10 border-b backdrop-blur",
          isFacilitatorView
            ? "border-accent bg-accent text-accent-foreground"
            : "border-border bg-white/90",
        )}
      >
        <div className="flex items-center justify-between px-6 py-3">
          {/* Wordmark */}
          <div className="flex items-center gap-8">
            <Link to="/program" className="group flex flex-col">
              <span
                className={cn(
                  "font-heading text-base leading-tight font-semibold",
                  isFacilitatorView ? "text-accent-foreground" : "text-foreground",
                )}
              >
                Tesla Program
              </span>
              <span
                className={cn("text-xs", isFacilitatorView ? "text-accent-light" : "text-muted")}
              >
                Innovation Leadership Program
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden items-center gap-1 md:flex">
              {allNavItems.map((item) =>
                item.href.startsWith("/modules") ? (
                  <ModulesMenu
                    key={item.href}
                    isFacilitatorView={isFacilitatorView}
                    isActive={isNavActive(item.href)}
                  />
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "px-3 py-2 text-sm font-medium transition-colors",
                      isFacilitatorView
                        ? isNavActive(item.href)
                          ? "text-accent-foreground border-accent-light border-b-2"
                          : "text-accent-light hover:text-accent-foreground"
                        : isNavActive(item.href)
                          ? "text-primary border-primary border-b-2"
                          : "text-muted hover:text-foreground",
                    )}
                  >
                    {item.label}
                  </Link>
                ),
              )}
            </nav>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {me && (me.first_name ?? me.last_name) && (
              <span
                className={cn(
                  "hidden text-sm sm:block",
                  isFacilitatorView ? "text-accent-light" : "text-muted",
                )}
              >
                {[me.first_name, me.last_name].filter(Boolean).join(" ")}
              </span>
            )}
            {me && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className={cn(
                  "hidden md:flex",
                  isFacilitatorView &&
                    "border-accent-light text-accent-foreground hover:bg-accent-light/10 hover:text-accent-foreground bg-transparent",
                )}
              >
                Sign out
              </Button>
            )}
            {!isFacilitatorView && (
              <Button asChild variant="outline" size="sm" className="hidden md:flex">
                <Link to="/login">Facilitator login</Link>
              </Button>
            )}
            {/* Mobile hamburger */}
            <button
              type="button"
              className={cn(
                "md:hidden",
                isFacilitatorView
                  ? "text-accent-light hover:text-accent-foreground"
                  : "text-muted hover:text-foreground",
              )}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div
            className={cn(
              "border-t px-4 pb-4 md:hidden",
              isFacilitatorView ? "border-accent-light/30 bg-accent" : "border-border bg-white",
            )}
          >
            <nav className="flex flex-col">
              {allNavItems.map((item) =>
                item.href.startsWith("/modules") ? (
                  <div
                    key={item.href}
                    className={cn(
                      "border-b py-2 last:border-0",
                      isFacilitatorView ? "border-accent-light/30" : "border-border",
                    )}
                  >
                    <span
                      className={cn(
                        "block py-1 text-sm font-semibold",
                        isFacilitatorView ? "text-accent-foreground" : "text-foreground",
                      )}
                    >
                      Modules
                    </span>
                    {MODULES.map((m) => (
                      <Link
                        key={m.id}
                        to={`/modules/${m.id}`}
                        onClick={() => setMenuOpen(false)}
                        className={cn(
                          "block py-2 pl-3 text-sm transition-colors",
                          isFacilitatorView
                            ? location.pathname === `/modules/${m.id}`
                              ? "text-accent-foreground"
                              : "text-accent-light"
                            : location.pathname === `/modules/${m.id}`
                              ? "text-primary"
                              : "text-muted",
                        )}
                      >
                        Module {m.id} — {m.title}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      "border-b py-3 text-sm font-medium transition-colors last:border-0",
                      isFacilitatorView
                        ? "border-accent-light/30 " +
                            (isNavActive(item.href)
                              ? "text-accent-foreground"
                              : "text-accent-light")
                        : cn(
                            "border-border",
                            isNavActive(item.href) ? "text-primary" : "text-muted",
                          ),
                    )}
                  >
                    {item.label}
                  </Link>
                ),
              )}
              {me && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className={cn(
                    "mt-3 text-left text-sm font-medium",
                    isFacilitatorView ? "text-accent-light" : "text-muted",
                  )}
                >
                  Sign out
                </button>
              )}
              {!isFacilitatorView && (
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="text-muted mt-3 text-left text-sm font-medium"
                >
                  Facilitator login
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>
    </div>
  );
}
